import { useEffect, useMemo, useState } from 'react';
import { getOpenDispatches, resolveTriageAsDispatch } from 'services/escriturasTriageService';
import { formatDateTime } from './format';

const OUTCOME_VALUES = ['signed', 'rejected', 'observation'];

/**
 * Registrar el resultado de notaría: el usuario le asigna el ESTADO de firma
 * (firmado/rechazado/con observaciones) a una escritura que está esperando firma.
 * El documento firmado solo aplica cuando el resultado es "Firmado":
 *   - 1 adjunto utilizable → se adjunta automático (se muestra, no se elige).
 *   - varios → ComboBox para elegir cuál es la escritura firmada (la notaría a veces
 *     manda boleta/carta además del documento).
 *   - ninguno → aviso: se registra firmado sin documento.
 */
export default function TriageResolveDispatchModal({ DesignSystemComponents, detail, t, onClose, onResolved, onSwitchToDismiss }) {
  const { Modal, Button, ComboBox } = DesignSystemComponents ?? {};

  const [dispatches, setDispatches] = useState([]);
  const [loadingDispatches, setLoadingDispatches] = useState(true);
  const [selectedDispatchId, setSelectedDispatchId] = useState('');
  const [outcome, setOutcome] = useState(
    OUTCOME_VALUES.includes(detail?.suggestedOutcome) ? detail.suggestedOutcome : 'signed'
  );

  // Adjuntos con copia en S3 (los únicos adjuntables como documento firmado).
  const eligibleAttachments = useMemo(
    () =>
      (detail?.attachments || [])
        .map((att, index) => ({ ...att, index }))
        .filter((att) => att.s3Key),
    [detail]
  );

  const defaultAttachmentIndex = useMemo(() => {
    const pdf = eligibleAttachments.find((a) => (a.contentType || '').toLowerCase().includes('pdf'));
    if (pdf) return String(pdf.index);
    return eligibleAttachments.length > 0 ? String(eligibleAttachments[0].index) : '';
  }, [eligibleAttachments]);
  const [attachmentIndex, setAttachmentIndex] = useState(defaultAttachmentIndex);

  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDispatches(true);
      try {
        const data = await getOpenDispatches();
        if (!cancelled) setDispatches(data || []);
      } catch {
        if (!cancelled) setDispatches([]);
      } finally {
        if (!cancelled) setLoadingDispatches(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const candidateIds = useMemo(
    () => new Set((detail?.candidates || []).map((c) => c.dispatchId)),
    [detail]
  );

  const dispatchOptions = useMemo(() => {
    const toOption = (d) => {
      const who = d.clientName || `${t('triage_resolve_unknown_client')} (caso ${d.caseId})`;
      const tipo = d.tipoEscritura ? ` — ${d.tipoEscritura}` : '';
      const notary = d.notaryName || d.notaryEmail || '';
      return {
        label:
          `${who}${tipo} — ${notary} — ${formatDateTime(d.sentAt)}` +
          (candidateIds.has(d.dispatchId) ? ` (${t('triage_resolve_candidate_tag')})` : ''),
        value: String(d.dispatchId),
      };
    };
    const candidates = dispatches.filter((d) => candidateIds.has(d.dispatchId)).map(toOption);
    const rest = dispatches.filter((d) => !candidateIds.has(d.dispatchId)).map(toOption);
    return [...candidates, ...rest];
  }, [dispatches, candidateIds, t]);

  const selectedDispatchOption =
    dispatchOptions.find((o) => o.value === selectedDispatchId) || null;

  const outcomeOptions = OUTCOME_VALUES.map((value) => ({
    label: t(`triage_resolve_outcome_${value}`),
    value,
  }));
  const selectedOutcomeOption = outcomeOptions.find((o) => o.value === outcome) || null;

  const attachmentOptions = useMemo(
    () =>
      eligibleAttachments.map((att) => ({
        label: att.fileName || `adjunto-${att.index + 1}`,
        value: String(att.index),
      })),
    [eligibleAttachments]
  );
  const selectedAttachmentOption =
    attachmentOptions.find((o) => o.value === attachmentIndex) || attachmentOptions[0] || null;

  const isSigned = outcome === 'signed';

  const handleConfirm = async () => {
    if (!selectedDispatchId) return;
    setSending(true);
    setError(null);
    try {
      const result = await resolveTriageAsDispatch(detail.id, {
        dispatchId: Number(selectedDispatchId),
        outcome,
        // El documento solo aplica al resultado "Firmado"; el cierre por
        // rechazo/observación no adjunta documento (igual que el automático).
        attachmentIndex: isSigned && attachmentIndex !== '' ? Number(attachmentIndex) : null,
        notes: notes || null,
      });
      onResolved(result?.message);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('triage_error_generic'));
      setSending(false);
    }
  };

  const renderCombo = (label, placeholder, options, value, onChange) =>
    ComboBox ? (
      <ComboBox
        label={label}
        placeholder={placeholder}
        noResultText={t('triage_resolve_dispatch_empty')}
        options={options}
        value={value}
        onChange={onChange}
      />
    ) : (
      <>
        <label className='form-label fw-semibold' style={{ fontSize: 13 }}>{label}</label>
        <select
          className='form-select form-select-sm'
          value={value?.value ?? ''}
          onChange={(e) => onChange(options.find((o) => o.value === e.target.value) || null)}
        >
          <option value=''>{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </>
    );

  // Sin escrituras esperando firma no hay a qué asignarle un resultado: el camino
  // correcto es descartar el correo (duplicado / no corresponde) — se ofrece directo.
  const emptyState = !loadingDispatches && dispatchOptions.length === 0;

  const body = emptyState ? (
    <div>
      <p className='font-light mb-2' style={{ color: '#B45309', fontSize: 13 }}>
        {t('triage_resolve_dispatch_empty')}
      </p>
      <p className='font-light mb-4' style={{ fontSize: 13 }}>
        {t('triage_resolve_empty_explain')}
      </p>
      <div className='d-flex justify-content-end align-items-center gap-3'>
        {Button ? (
          <Button label={t('triage_btn_cancel')} size='sm' variant='secondary' onClick={onClose} />
        ) : (
          <button type='button' className='btn btn-outline-secondary btn-sm' onClick={onClose}>
            {t('triage_btn_cancel')}
          </button>
        )}
        <button
          type='button'
          className='btn btn-link btn-sm p-0'
          style={{ color: 'var(--error-500, #DC2626)', textDecoration: 'none', fontSize: 13 }}
          onClick={onSwitchToDismiss}
        >
          {t('triage_btn_dismiss_from_here')}
        </button>
      </div>
    </div>
  ) : (
    <div>
      <p className='font-light mb-3' style={{ fontSize: 13 }}>{t('triage_resolve_hint')}</p>

      {error && <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>{error}</div>}

      {loadingDispatches ? (
        <p className='font-light'>{t('triage_sending')}</p>
      ) : (
        <div className='mb-2'>
          {renderCombo(
            t('triage_resolve_dispatch_label'),
            t('triage_resolve_dispatch_placeholder'),
            dispatchOptions,
            selectedDispatchOption,
            (selected) => setSelectedDispatchId(selected?.value != null ? String(selected.value) : '')
          )}
        </div>
      )}

      <div className='mb-2'>
        {renderCombo(
          t('triage_resolve_outcome_label'),
          '—',
          outcomeOptions,
          selectedOutcomeOption,
          (selected) => setOutcome(selected?.value || 'signed')
        )}
      </div>

      {isSigned && (
        <div className='mb-2'>
          {eligibleAttachments.length === 0 ? (
            <p className='font-light mb-2' style={{ color: '#B45309', fontSize: 13 }}>
              {t('triage_resolve_no_doc_warning')}
            </p>
          ) : eligibleAttachments.length === 1 ? (
            <p className='mb-2' style={{ fontSize: 13 }}>
              <strong>{t('triage_resolve_attachment_label')}:</strong>{' '}
              {eligibleAttachments[0].fileName || 'adjunto-1'}
            </p>
          ) : (
            renderCombo(
              t('triage_resolve_attachment_label'),
              '—',
              attachmentOptions,
              selectedAttachmentOption,
              (selected) => setAttachmentIndex(selected?.value ?? '')
            )
          )}
        </div>
      )}

      <label className='form-label fw-semibold' style={{ fontSize: 13 }}>
        {t('triage_resolve_notes_label')}
      </label>
      <textarea
        className='form-control form-control-sm mb-3'
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      <div className='d-flex justify-content-end gap-2'>
        {Button ? (
          <>
            <Button label={t('triage_btn_cancel')} size='sm' variant='secondary' onClick={onClose} disabled={sending} />
            <Button
              label={sending ? t('triage_sending') : t('triage_btn_register_outcome')}
              size='sm'
              variant='primary'
              onClick={handleConfirm}
              disabled={sending || !selectedDispatchId}
            />
          </>
        ) : (
          <>
            <button type='button' className='btn btn-outline-secondary btn-sm' onClick={onClose} disabled={sending}>
              {t('triage_btn_cancel')}
            </button>
            <button type='button' className='btn btn-primary btn-sm' onClick={handleConfirm} disabled={sending || !selectedDispatchId}>
              {sending ? t('triage_sending') : t('triage_btn_register_outcome')}
            </button>
          </>
        )}
      </div>
    </div>
  );

  if (!Modal) {
    return (
      <div
        role='dialog'
        style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32,
        }}
        onClick={onClose}
      >
        <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 20, width: 'min(640px, 92vw)' }}>
          <h5>{t('triage_resolve_title')}</h5>
          {body}
        </div>
      </div>
    );
  }
  return (
    <Modal title={t('triage_resolve_title')} show onClose={onClose} size='lg'>
      {body}
    </Modal>
  );
}
